import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, Sphere, MeshDistortMaterial, Trail } from '@react-three/drei';
import * as THREE from 'three';
import { useInView } from 'framer-motion';

const Orb = ({ position, color, speed, distort }) => {
  const meshRef = useRef();
  
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    meshRef.current.position.y = position[1] + Math.sin(t * speed) * 0.5;
    meshRef.current.position.x = position[0] + Math.cos(t * speed) * 0.3;
  });

  return (
    <Float speed={2} rotationIntensity={1} floatIntensity={1}>
      <mesh ref={meshRef} position={position}>
        <sphereGeometry args={[1, 32, 32]} />
        <MeshDistortMaterial
          color={color}
          speed={distort}
          distort={0.4}
          radius={1}
        />
      </mesh>
    </Float>
  );
};

const Particles = ({ count = 50 }) => {
  const points = useMemo(() => {
    const p = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      p[i * 3] = (Math.random() - 0.5) * 10;
      p[i * 3 + 1] = (Math.random() - 0.5) * 10;
      p[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
    return p;
  }, [count]);

  const pointsRef = useRef();

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    pointsRef.current.rotation.y = t * 0.05;
    pointsRef.current.rotation.x = t * 0.03;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={points}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        color="#3b82f6"
        transparent
        opacity={0.5}
        sizeAttenuation
      />
    </points>
  );
};

const Scene3D = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef);

  return (
    <div ref={containerRef} className="absolute inset-0 z-0 opacity-40">
      <Canvas 
        frameloop={isInView ? "always" : "demand"}
        camera={{ position: [0, 0, 5], fov: 75 }}
        dpr={[1, 2]} // Limit pixel density to 2x for performance
        gl={{ 
          antialias: false, // Performance over perfect edges
          powerPreference: "high-performance" 
        }}
      >
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} />
        <Orb position={[-2, 1, 0]} color="#3b82f6" speed={1.5} distort={2} />
        <Orb position={[2, -1, -1]} color="#60a5fa" speed={1} distort={1.5} />
        <Particles count={60} />
      </Canvas>
    </div>
  );
};

export default Scene3D;
